<script lang="ts" setup>
import type { AuthSteps } from "./MPAuth.vue";
import { AuthError } from "@supabase/supabase-js";
import {
  reactive,
  ref,
  showError,
  useI18n,
  useDialog,
  useMetrics,
  useAuth,
} from "#imports";
import MPPrivacyPolicy from "../MPPrivacyPolicy.vue";
import MPMessage from "~/components/widgets/MPMessage.vue";
import MPInput from "~/components/forms/MPInput.vue";
import MPCheckbox from "~/components/forms/MPCheckbox.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPImg from "~/components/widgets/MPImg.vue";
import {
  isEmail,
  isMaxStringLength,
  isMinStringLength,
  isRequired,
  isTrue,
} from "~/components/forms/validation";

const props = defineProps<{
  userType: string;
}>();

const emit = defineEmits<{
  toggleAuthStep: [value: AuthSteps];
  startOnboarding: [];
}>();

const { t } = useI18n();

const signUpError = ref<AuthError | null>(null);

// Signup form
const signupModel = reactive({
  email: "",
  password: "",
  confirmPassword: "",
  privacyPolicyAccepted: false,
});

const authStore = useAuth();
const { trackEvent } = useMetrics();
const { openDialog, closeDialog } = useDialog();

const isLoading = ref(false);
const isSent = ref(false);
const signupFailed = ref(false);
const signupSuccess = ref(false);

const handleSignup = async () => {
  isLoading.value = true;
  try {
    await authStore.signUp(signupModel);
    isSent.value = true;
    signupFailed.value = false;
    signupSuccess.value = true;
    trackEvent("joined");
    if (props.userType === "creator") {
      emit("startOnboarding");
    } else {
      await new Promise((resolve) =>
        setTimeout(() => {
          closeDialog();
          resolve(undefined);
        }, 500),
      );
    }
  } catch (e) {
    if (e instanceof AuthError) {
      signUpError.value = e;
    } else {
      showError("Failed to log privacy policy agreement");
    }
  } finally {
    isLoading.value = false;
  }
};

const openPrivacyModal = () => {
  openDialog(MPPrivacyPolicy, {});
};
</script>

<template>
  <div>
    <MPMessage class="error-message" v-if="signupFailed" version="error">
      <p>
        {{ t("auth.signup.signupFailed") }}
      </p>
    </MPMessage>
    <MPMessage class="success-message" v-if="isSent" version="success">
      <p>Check your email for a link to confirm your account</p>
    </MPMessage>
    <div class="title">
      <h3>
        Real <span>places</span> by <br />
        real <span>people</span>
      </h3>
      <p>
        Follow a collective of creators, explorers and locals mapping the
        world's best spots
      </p>
    </div>
    <form
      v-if="!isSent && !isLoading"
      class="signup"
      @submit.prevent="handleSignup"
    >
      <MPInput
        v-model="signupModel.email"
        name="email"
        type="email"
        :placeholder="t('auth.login.email')"
        class="input-email"
        :validate="(v) => isRequired(v) || isEmail(v)"
      />
      <MPInput
        v-model="signupModel.password"
        name="password"
        type="password"
        :placeholder="t('auth.login.password')"
        :validate="
          (v) =>
            isRequired(v) ||
            isMinStringLength(8)(v) ||
            isMaxStringLength(255)(v)
        "
      />
      <MPInput
        v-model="signupModel.confirmPassword"
        name="confirmPassword"
        type="password"
        :placeholder="t('auth.login.confirmPassword')"
        :validate="
          (v) =>
            isRequired(v) ||
            (signupModel.password !== v ? 'Passwords must match' : undefined)
        "
      />
      <div class="privacy-policy">
        <MPCheckbox
          v-model="signupModel.privacyPolicyAccepted"
          name="privacyPolicyAccepted"
          label="I agree to the"
          :validate="
            (v) => (isTrue(v) ? 'Privacy policy is required' : undefined)
          "
        >
          <template #helpText>
            <NuxtLink to="/info/privacy-policy">Privacy Policy</NuxtLink>
          </template>
        </MPCheckbox>
        <span @click="openPrivacyModal" class="typeface-regular"
          ><i class="ri-information-2-line" />&nbsp;Info</span
        >
      </div>
      <MPButton
        :label="t('auth.signup.title')"
        type="submit"
        :disabled="isLoading"
        :isLoading="isLoading"
      />
      <div class="divider">
        <hr />
        <small>or</small>
        <hr />
      </div>
      <MPButton
        label="Login"
        version="secondary"
        @click="emit('toggleAuthStep', 'login')"
      />

      <div class="social-oauth">
        <MPImg
          src="/media/images/google-social-auth.svg"
          @click="authStore.signInWithOAuth('google')"
        />
        <!--
        <MPImg
          src="/media/images/facebook-social-auth.svg"
          @click="socialAuth('facebook')"
        />-->
      </div>
      <small class="auth-footer">
        {{ t("auth.signup.agreeToTerms") }}
      </small>
    </form>
    <div v-if="isLoading && !signupSuccess" class="loading-screen">
      <MPImg src="/media/images/authenticating-graphic.svg" />
      <p>Authenticating</p>
    </div>
    <div v-if="isLoading && signupSuccess" class="loading-success">
      <MPImg src="/media/images/authenticated-graphic.svg" />
      <p>You are signed in</p>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.signup {
  display: grid;
  gap: 1rem;
  padding: 2rem;
  padding-top: 1.5rem;
}

.title {
  color: var(--color-slate-500);
  h3 {
    margin: 0;
    margin-top: 2rem;
    margin-bottom: 0.5rem;
    span {
      color: var(--color-rose-400);
    }
  }
  p {
    margin: 0;
    padding-inline: 3rem;
    margin-top: 1rem;
    color: var(--color-slate-400);
    line-height: 1.2 !important;
  }
}

.divider {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  color: var(--color-slate-500);
}

.auth-footer {
  color: var(--color-slate-400);
}

.error-message {
  margin-bottom: 1rem;
}

.success-message {
  margin-bottom: 1rem;
}

.privacy-policy {
  position: relative;
  display: flex;
  align-items: center;
  justify-items: start;
  gap: 0.5rem;
  font-size: var(--font-size-9);

  span {
    position: absolute;
    top: 50%;
    right: -1.5rem;
    transform: translate(-50%, -50%);
    color: var(--color-slate-500);
    background-color: var(--color-slate-50);
    padding: 0.25rem 0.25rem;
    border-radius: 0.25rem;
    cursor: pointer;
  }
}

.social-oauth {
  display: flex;
  gap: 0.5rem;
  margin: auto;
  img {
    cursor: pointer;
  }
}
.loading-screen {
  img {
    margin: auto;
    width: 80%;
  }

  p {
    color: var(--color-rose-400);
  }
}
</style>
